import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import Button from "@/components/site/Button";
import PagesEditor from "@/components/admin/PagesEditor";
import BlogAdmin from "@/components/admin/BlogAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Module-scoped guard: prevents React StrictMode dev-time double-mount from
// consuming the magic-link token twice.
const _verifiedTokens = new Set();

const DAG_NAMEN = [
    "Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag",
];

const MAANDEN = [
    "jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec",
];

function fmt(iso) {
    const d = new Date(iso);
    return `${d.getDate()} ${MAANDEN[d.getMonth()]} ${d.getFullYear()}, ${d.toLocaleTimeString(
        "nl-NL",
        { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" }
    )}`;
}

export default function Admin() {
    // authStatus: null=checking, "authed"=logged in, "anon"=needs login
    const [authStatus, setAuthStatus] = useState(null);
    const [adminEmail, setAdminEmail] = useState("");
    const [tab, setTab] = useState("boekingen");
    const [navOpen, setNavOpen] = useState(false);
    // magic-link form
    const [email, setEmail] = useState("");
    const [linkSent, setLinkSent] = useState(false);
    const [linkError, setLinkError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // verify state
    const [verifyState, setVerifyState] = useState(null); // null | "verifying" | "success" | "error"
    const [verifyError, setVerifyError] = useState("");
    const verifyStartedRef = useRef(false);

    // noindex
    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        document.head.appendChild(meta);
        return () => document.head.removeChild(meta);
    }, []);

    // Detect ?token=... path for verify flow
    useEffect(() => {
        if (!window.location.pathname.endsWith("/verify")) return;
        if (verifyStartedRef.current) return;
        verifyStartedRef.current = true;
        const params = new URLSearchParams(window.location.search);
        const t = params.get("token");
        if (!t) {
            setVerifyState("error");
            setVerifyError("Ongeldige inloglink.");
            return;
        }
        if (_verifiedTokens.has(t)) return;
        _verifiedTokens.add(t);
        setVerifyState("verifying");
        axios
            .get(`${API}/admin/verify?token=${encodeURIComponent(t)}`, {
                withCredentials: true,
            })
            .then((r) => {
                setAdminEmail(r.data.email || "");
                setAuthStatus("authed");
                setVerifyState("success");
                window.history.replaceState({}, "", "/admin");
            })
            .catch((err) => {
                // Dev-only React StrictMode may double-fire; if a session was
                // established by the first (successful) call, treat as success.
                axios
                    .get(`${API}/admin/me`, { withCredentials: true })
                    .then((me) => {
                        setAdminEmail(me.data.email || "");
                        setAuthStatus("authed");
                        setVerifyState("success");
                        window.history.replaceState({}, "", "/admin");
                    })
                    .catch(() => {
                        setVerifyState("error");
                        setVerifyError(
                            err.response?.data?.detail ||
                                "Deze inloglink is ongeldig."
                        );
                    });
            });
    }, []);

    // Check existing session on mount (skip if verify flow active)
    useEffect(() => {
        if (window.location.pathname.endsWith("/verify")) return;
        axios
            .get(`${API}/admin/me`, { withCredentials: true })
            .then((r) => {
                setAdminEmail(r.data.email || "");
                setAuthStatus("authed");
            })
            .catch(() => setAuthStatus("anon"));
    }, []);

    const requestLink = async () => {
        setLinkError("");
        if (!emailOk(email)) {
            setLinkError("Vul een geldig e-mailadres in.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/admin/request-link`,
                { email: email.trim().toLowerCase() },
                { withCredentials: true }
            );
            setLinkSent(true);
        } catch (err) {
            setLinkError(
                err.response?.data?.detail ||
                    "Er ging iets mis. Probeer het opnieuw."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
        } catch {
            /* ignore */
        }
        setAuthStatus("anon");
        setAdminEmail("");
        setLinkSent(false);
        setEmail("");
    };

    // ----- Verify sub-view -----
    if (verifyState === "verifying") {
        return (
            <section
                className="mx-auto max-w-[420px] px-5 pt-32 md:pt-48 pb-32 min-h-[70vh]"
                data-testid="admin-verify-loading"
            >
                <p className="text-[var(--hds-ink)]/70">Bezig met inloggen...</p>
            </section>
        );
    }
    if (verifyState === "error") {
        return (
            <section
                className="mx-auto max-w-[440px] px-5 pt-32 md:pt-48 pb-32 min-h-[70vh]"
                data-testid="admin-verify-error"
            >
                <h1
                    className="font-[Fraunces] text-[36px] leading-tight mb-6"
                    style={{ fontWeight: 300 }}
                >
                    Inloggen mislukt
                </h1>
                <p className="mb-8 text-[15.5px] leading-[1.6] text-[var(--hds-ink)]/80">
                    {verifyError}
                </p>
                <Button
                    variant="primary"
                    icon="arrow"
                    to="/admin"
                    data-testid="admin-verify-back"
                >
                    Terug naar inloggen
                </Button>
            </section>
        );
    }

    // ----- Auth check in progress -----
    if (authStatus === null) {
        return (
            <section className="mx-auto max-w-[420px] px-5 pt-32 pb-32 min-h-[70vh]">
                <p className="text-[var(--hds-ink)]/50">Even geduld...</p>
            </section>
        );
    }

    // ----- Anonymous: magic-link form -----
    if (authStatus === "anon") {
        return (
            <section
                className="mx-auto max-w-[440px] px-5 pt-32 md:pt-48 pb-32 min-h-[70vh]"
                data-testid="admin-login"
            >
                <h1
                    className="font-[Fraunces] text-[40px] leading-tight mb-4"
                    style={{ fontWeight: 300 }}
                >
                    Beheer
                </h1>
                <p className="text-[15px] leading-[1.6] text-[var(--hds-ink)]/70 mb-8">
                    Voer je e-mailadres in en ontvang een eenmalige inloglink.
                </p>
                {linkSent ? (
                    <div
                        className="rounded-[12px] bg-[var(--hds-green)]/10 border border-[var(--hds-green)]/30 p-5 text-[15px] leading-[1.6] text-[var(--hds-ink)]"
                        data-testid="admin-login-sent"
                    >
                        Als dit adres toegang heeft, staat er een inloglink in je mail.
                    </div>
                ) : (
                    <>
                        <label
                            htmlFor="admin-email"
                            className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2"
                        >
                            E-mailadres
                        </label>
                        <input
                            id="admin-email"
                            type="email"
                            data-testid="admin-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && requestLink()}
                            autoComplete="email"
                            className="w-full rounded-[12px] bg-[var(--hds-card)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[15px] mb-4"
                        />
                        {linkError && (
                            <p
                                className="mb-4 text-[13px] text-[var(--hds-clay-dark)]"
                                data-testid="admin-login-error"
                            >
                                {linkError}
                            </p>
                        )}
                        <Button
                            variant="primary"
                            icon="arrow"
                            onClick={requestLink}
                            disabled={submitting}
                            data-testid="admin-request-link-btn"
                        >
                            {submitting ? "Bezig..." : "Stuur inloglink"}
                        </Button>
                    </>
                )}
            </section>
        );
    }

    // ----- Authed: dashboard met zijbalk -----
    const NAV_GROUPS = [
        {
            group: "Aanvragen",
            items: [
                ["boekingen", "Boekingen"],
                ["qa", "Q&A-aanmeldingen"],
                ["contact", "Contactberichten"],
                ["waitlist", "Workshop-wachtlijst"],
            ],
        },
        { group: "Agenda", items: [["beschikbaarheid", "Beschikbaarheid"]] },
        { group: "Website", items: [["paginas", "Pagina's"], ["blog", "Blog"]] },
    ];

    const sidebar = (
        <aside
            data-testid="admin-sidebar"
            className={`fixed md:sticky top-0 md:top-0 left-0 z-40 h-[100svh] md:h-auto md:self-start w-[260px] shrink-0 bg-[var(--hds-card)] md:bg-transparent border-r border-[var(--hds-line)] px-6 py-8 md:py-2 flex flex-col transition-transform duration-300 ${
                navOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
            }`}
        >
            <div className="flex items-center justify-between mb-8 md:mb-6">
                <span
                    className="font-[Fraunces] text-[22px] text-[var(--hds-ink)]"
                    style={{ fontWeight: 400 }}
                >
                    Beheer
                </span>
                <button
                    type="button"
                    onClick={() => setNavOpen(false)}
                    data-testid="admin-nav-close"
                    className="md:hidden text-[13px] text-[var(--hds-ink)]/60 underline underline-offset-2"
                >
                    Sluiten
                </button>
            </div>
            <nav className="flex-1 space-y-7 overflow-y-auto" aria-label="Beheer navigatie">
                {NAV_GROUPS.map((g) => (
                    <div key={g.group}>
                        <p className="text-[10.5px] tracking-[0.24em] uppercase text-[var(--hds-ink)]/45 mb-2.5">
                            {g.group}
                        </p>
                        <ul className="space-y-1">
                            {g.items.map(([key, label]) => (
                                <li key={key}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTab(key);
                                            setNavOpen(false);
                                        }}
                                        data-testid={`admin-tab-${key}`}
                                        className={`w-full text-left px-3 py-2 rounded-[8px] text-[14.5px] transition-colors duration-300 border-l-2 ${
                                            tab === key
                                                ? "border-[var(--hds-clay)] bg-[var(--hds-sand)] text-[var(--hds-ink)]"
                                                : "border-transparent text-[var(--hds-ink)]/60 hover:text-[var(--hds-ink)] hover:bg-[var(--hds-sand)]/60"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>
            <div className="pt-6 mt-6 border-t border-[var(--hds-line)]">
                {adminEmail && (
                    <p
                        className="text-[12.5px] text-[var(--hds-ink)]/55 mb-3 break-all"
                        data-testid="admin-email-display"
                    >
                        {adminEmail}
                    </p>
                )}
                <button
                    type="button"
                    onClick={logout}
                    data-testid="admin-logout"
                    className="text-[13px] text-[var(--hds-ink)]/60 hover:text-[var(--hds-clay)] underline underline-offset-2"
                >
                    Uitloggen
                </button>
            </div>
        </aside>
    );

    return (
        <div data-testid="admin-panel" className="pt-20 md:pt-28 pb-24 md:flex md:gap-10 md:px-10 md:max-w-[1440px] md:mx-auto">
            {/* mobiele bovenbalk */}
            <div className="md:hidden flex items-center justify-between px-5 pb-5">
                <span className="font-[Fraunces] text-[20px]" style={{ fontWeight: 400 }}>
                    Beheer
                </span>
                <button
                    type="button"
                    onClick={() => setNavOpen(true)}
                    data-testid="admin-nav-toggle"
                    className="inline-flex items-center gap-2 rounded-full border border-[var(--hds-line)] bg-[var(--hds-card)] px-4 py-2 text-[13.5px] text-[var(--hds-ink)]"
                >
                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden>
                        <path d="M1 1h12M1 6h12M1 11h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                    Menu
                </button>
            </div>
            {navOpen && (
                <div
                    aria-hidden
                    onClick={() => setNavOpen(false)}
                    className="md:hidden fixed inset-0 z-30 bg-[rgba(44,39,34,0.35)]"
                />
            )}
            {sidebar}
            <main className="flex-1 min-w-0 px-5 md:px-0">
                {tab === "boekingen" && <Bookings onUnauthorized={logout} />}
                {tab === "qa" && <QASignups onUnauthorized={logout} />}
                {tab === "contact" && <Contacts onUnauthorized={logout} />}
                {tab === "waitlist" && <WorkshopWaitlist onUnauthorized={logout} />}
                {tab === "beschikbaarheid" && <Availability onUnauthorized={logout} />}
                {tab === "paginas" && <PagesEditor onUnauthorized={logout} />}
                {tab === "blog" && <BlogAdmin onUnauthorized={logout} />}
            </main>
        </div>
    );
}

function downloadCSV(filename, columns, rows) {
    const esc = (v) => {
        const s = v === null || v === undefined ? "" : String(v);
        return `"${s.replace(/"/g, '""')}"`;
    };
    const header = columns.map((c) => esc(c.label)).join(",");
    const body = rows
        .map((r) => columns.map((c) => esc(r[c.key])).join(","))
        .join("\n");
    const csv = `${header}\n${body}`;
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function QASignups({ onUnauthorized }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get(`${API}/admin/qa-signups`, { withCredentials: true })
            .then((r) => setRows(r.data.signups))
            .catch((err) => err.response?.status === 401 && onUnauthorized())
            .finally(() => setLoading(false));
    }, [onUnauthorized]);

    const cols = [
        { key: "aangemaakt_op", label: "Aangemaakt" },
        { key: "naam", label: "Naam" },
        { key: "email", label: "E-mail" },
        { key: "vraag", label: "Vraag" },
        { key: "nieuwsbrief_opt_in", label: "Nieuwsbrief" },
        { key: "akkoord_op", label: "Akkoord op" },
    ];

    if (loading) return <p className="text-[var(--hds-ink)]/60">Laden...</p>;

    return (
        <div data-testid="admin-qa">
            <div className="flex justify-end mb-4">
                <Button variant="secondary" data-testid="qa-csv" onClick={() => downloadCSV("qa-aanmeldingen.csv", cols, rows)}>
                    Download CSV
                </Button>
            </div>
            {rows.length === 0 ? (
                <p className="text-[var(--hds-ink)]/60" data-testid="admin-no-qa">Nog geen aanmeldingen.</p>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div key={r.id} data-testid={`admin-qa-${r.id}`} className="rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-6">
                            <div className="flex items-baseline justify-between gap-4 mb-1">
                                <p className="font-medium">{r.naam}</p>
                                <span className="text-[13px] text-[var(--hds-ink)]/50">{fmt(r.aangemaakt_op)}</span>
                            </div>
                            <p className="text-[14px] text-[var(--hds-ink)]/70">{r.email}</p>
                            {r.vraag && <p className="mt-2 text-[14px] italic text-[var(--hds-ink)]/65">&ldquo;{r.vraag}&rdquo;</p>}
                            <p className="mt-2 text-[12.5px] text-[var(--hds-ink)]/50">
                                Nieuwsbrief: {r.nieuwsbrief_opt_in ? "ja" : "nee"}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Contacts({ onUnauthorized }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get(`${API}/admin/contact-messages`, { withCredentials: true })
            .then((r) => setRows(r.data.messages))
            .catch((err) => err.response?.status === 401 && onUnauthorized())
            .finally(() => setLoading(false));
    }, [onUnauthorized]);

    const cols = [
        { key: "aangemaakt_op", label: "Aangemaakt" },
        { key: "naam", label: "Naam" },
        { key: "email", label: "E-mail" },
        { key: "telefoon", label: "Telefoon" },
        { key: "onderwerp", label: "Onderwerp" },
        { key: "bericht", label: "Bericht" },
        { key: "akkoord_op", label: "Akkoord op" },
    ];

    if (loading) return <p className="text-[var(--hds-ink)]/60">Laden...</p>;

    return (
        <div data-testid="admin-contact">
            <div className="flex justify-end mb-4">
                <Button variant="secondary" data-testid="contact-csv" onClick={() => downloadCSV("contactberichten.csv", cols, rows)}>
                    Download CSV
                </Button>
            </div>
            {rows.length === 0 ? (
                <p className="text-[var(--hds-ink)]/60" data-testid="admin-no-contact">Nog geen berichten.</p>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div key={r.id} data-testid={`admin-contact-${r.id}`} className="rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-6">
                            <div className="flex items-baseline justify-between gap-4 mb-1">
                                <p className="font-medium">{r.naam} <span className="text-[var(--hds-clay)] text-[13px]">· {r.onderwerp}</span></p>
                                <span className="text-[13px] text-[var(--hds-ink)]/50">{fmt(r.aangemaakt_op)}</span>
                            </div>
                            <p className="text-[14px] text-[var(--hds-ink)]/70">{r.email}{r.telefoon ? ` · ${r.telefoon}` : ""}</p>
                            <p className="mt-2 text-[14px] text-[var(--hds-ink)]/75 leading-[1.6]">{r.bericht}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function WorkshopWaitlist({ onUnauthorized }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get(`${API}/admin/workshop-waitlist`, { withCredentials: true })
            .then((r) => setRows(r.data.entries))
            .catch((err) => err.response?.status === 401 && onUnauthorized())
            .finally(() => setLoading(false));
    }, [onUnauthorized]);

    const cols = [
        { key: "aangemaakt_op", label: "Aangemaakt" },
        { key: "naam", label: "Naam" },
        { key: "email", label: "E-mail" },
        { key: "akkoord_op", label: "Akkoord op" },
    ];

    if (loading) return <p className="text-[var(--hds-ink)]/60">Laden...</p>;

    return (
        <div data-testid="admin-waitlist">
            <div className="flex justify-end mb-4">
                <Button variant="secondary" data-testid="waitlist-csv" onClick={() => downloadCSV("workshop-wachtlijst.csv", cols, rows)}>
                    Download CSV
                </Button>
            </div>
            {rows.length === 0 ? (
                <p className="text-[var(--hds-ink)]/60" data-testid="admin-no-waitlist">Nog geen wachtlijst-aanmeldingen.</p>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div key={r.id} data-testid={`admin-waitlist-${r.id}`} className="rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-6">
                            <div className="flex items-baseline justify-between gap-4 mb-1">
                                <p className="font-medium">{r.naam}</p>
                                <span className="text-[13px] text-[var(--hds-ink)]/50">{fmt(r.aangemaakt_op)}</span>
                            </div>
                            <p className="text-[14px] text-[var(--hds-ink)]/70">{r.email}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function Bookings({ onUnauthorized }) {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        axios
            .get(`${API}/admin/bookings`, { withCredentials: true })
            .then((r) => setBookings(r.data.bookings))
            .catch((err) => {
                if (err.response?.status === 401) onUnauthorized();
            })
            .finally(() => setLoading(false));
    }, [onUnauthorized]);

    useEffect(() => load(), [load]);

    const changeStatus = async (id, status) => {
        try {
            await axios.patch(
                `${API}/admin/bookings/${id}`,
                { status },
                { withCredentials: true }
            );
            load();
        } catch (err) {
            if (err.response?.status === 401) onUnauthorized();
        }
    };

    if (loading) return <p className="text-[var(--hds-ink)]/60">Laden...</p>;
    if (bookings.length === 0)
        return <p className="text-[var(--hds-ink)]/60" data-testid="admin-no-bookings">Nog geen boekingen.</p>;

    return (
        <div className="space-y-4" data-testid="admin-bookings">
            {bookings.map((b) => (
                <div
                    key={b.id}
                    data-testid={`admin-booking-${b.id}`}
                    className="rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
                >
                    <div className="md:col-span-4">
                        <p className="font-[Fraunces] text-[20px]" style={{ fontWeight: 400 }}>
                            {b.aanbod_naam}
                        </p>
                        <p className="text-[14px] text-[var(--hds-ink)]/70">{fmt(b.starttijd)}</p>
                        <p className="text-[13px] text-[var(--hds-ink)]/50">{b.bedrag} euro</p>
                    </div>
                    <div className="md:col-span-5 text-[13.5px] text-[var(--hds-ink)]/75 leading-[1.6]">
                        <p>{b.naam} · {b.email} · {b.telefoon}</p>
                        <p className="text-[var(--hds-ink)]/55">
                            Geboren: {b.geboortedatum} {b.geboortetijd}, {b.geboorteplaats}
                        </p>
                        {b.vraag && <p className="italic text-[var(--hds-ink)]/55">&ldquo;{b.vraag}&rdquo;</p>}
                    </div>
                    <div className="md:col-span-3 flex md:justify-end items-start">
                        <select
                            value={b.status}
                            data-testid={`admin-status-${b.id}`}
                            onChange={(e) => changeStatus(b.id, e.target.value)}
                            className="rounded-full border border-[var(--hds-line)] bg-[var(--hds-sand)] px-4 py-2 text-[13.5px] outline-none focus:border-[var(--hds-clay)]"
                        >
                            <option value="reservering">Reservering</option>
                            <option value="betaald">Betaald</option>
                            <option value="geannuleerd">Geannuleerd</option>
                        </select>
                    </div>
                </div>
            ))}
        </div>
    );
}

function Availability({ onUnauthorized }) {
    const [avail, setAvail] = useState(null);
    const [saved, setSaved] = useState(false);
    const [newException, setNewException] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/admin/availability`, { withCredentials: true })
            .then((r) => {
                const a = r.data;
                // normalise to single window per day for the UI
                const days = {};
                for (let i = 0; i < 7; i++) {
                    const rules = a.week_rules?.[String(i)] || [];
                    days[i] = rules.length
                        ? { open: true, start: rules[0].start, end: rules[0].end }
                        : { open: false, start: "09:00", end: "17:00" };
                }
                setAvail({
                    days,
                    exceptions: a.exceptions || [],
                    lead_time_uren: a.lead_time_uren ?? 24,
                    buffer_minuten: a.buffer_minuten ?? 15,
                });
            })
            .catch((err) => {
                if (err.response?.status === 401) onUnauthorized();
            });
    }, [onUnauthorized]);

    const save = async () => {
        setSaved(false);
        const week_rules = {};
        for (let i = 0; i < 7; i++) {
            const d = avail.days[i];
            week_rules[String(i)] = d.open ? [{ start: d.start, end: d.end }] : [];
        }
        try {
            await axios.put(
                `${API}/admin/availability`,
                {
                    week_rules,
                    exceptions: avail.exceptions,
                    lead_time_uren: Number(avail.lead_time_uren),
                    buffer_minuten: Number(avail.buffer_minuten),
                },
                { withCredentials: true }
            );
            setSaved(true);
        } catch (err) {
            if (err.response?.status === 401) onUnauthorized();
        }
    };

    if (!avail) return <p className="text-[var(--hds-ink)]/60">Laden...</p>;

    const setDay = (i, patch) =>
        setAvail({ ...avail, days: { ...avail.days, [i]: { ...avail.days[i], ...patch } } });

    return (
        <div data-testid="admin-availability" className="space-y-10 max-w-[720px]">
            <div>
                <h2 className="font-[Fraunces] text-[24px] mb-5" style={{ fontWeight: 400 }}>
                    Weekdagen
                </h2>
                <div className="space-y-3">
                    {DAG_NAMEN.map((naam, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-3 rounded-[12px] border border-[var(--hds-line)] p-4">
                            <label className="flex items-center gap-2 w-32">
                                <input
                                    type="checkbox"
                                    data-testid={`day-open-${i}`}
                                    checked={avail.days[i].open}
                                    onChange={(e) => setDay(i, { open: e.target.checked })}
                                    className="w-4 h-4 accent-[var(--hds-clay)]"
                                />
                                <span className="text-[14px]">{naam}</span>
                            </label>
                            {avail.days[i].open && (
                                <div className="flex items-center gap-2 text-[14px]">
                                    <input
                                        type="time"
                                        data-testid={`day-start-${i}`}
                                        value={avail.days[i].start}
                                        onChange={(e) => setDay(i, { start: e.target.value })}
                                        className="rounded-[10px] border border-[var(--hds-line)] bg-[var(--hds-card)] px-3 py-1.5 outline-none focus:border-[var(--hds-clay)]"
                                    />
                                    <span className="text-[var(--hds-ink)]/50">tot</span>
                                    <input
                                        type="time"
                                        data-testid={`day-end-${i}`}
                                        value={avail.days[i].end}
                                        onChange={(e) => setDay(i, { end: e.target.value })}
                                        className="rounded-[10px] border border-[var(--hds-line)] bg-[var(--hds-card)] px-3 py-1.5 outline-none focus:border-[var(--hds-clay)]"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <h2 className="font-[Fraunces] text-[24px] mb-5" style={{ fontWeight: 400 }}>
                    Geblokkeerde datums
                </h2>
                <div className="flex flex-wrap gap-2 mb-4">
                    {avail.exceptions.map((ex) => (
                        <span key={ex} className="inline-flex items-center gap-2 rounded-full bg-[var(--hds-clay)]/10 border border-[var(--hds-clay)]/30 px-3 py-1.5 text-[13px]">
                            {ex}
                            <button
                                type="button"
                                onClick={() => setAvail({ ...avail, exceptions: avail.exceptions.filter((x) => x !== ex) })}
                                className="text-[var(--hds-clay-dark)]"
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    {avail.exceptions.length === 0 && (
                        <span className="text-[14px] text-[var(--hds-ink)]/50">Geen geblokkeerde datums.</span>
                    )}
                </div>
                <div className="flex gap-2">
                    <input
                        type="date"
                        data-testid="exception-input"
                        value={newException}
                        onChange={(e) => setNewException(e.target.value)}
                        className="rounded-[10px] border border-[var(--hds-line)] bg-[var(--hds-card)] px-3 py-2 text-[14px] outline-none focus:border-[var(--hds-clay)]"
                    />
                    <Button
                        variant="secondary"
                        data-testid="add-exception"
                        onClick={() => {
                            if (newException && !avail.exceptions.includes(newException)) {
                                setAvail({ ...avail, exceptions: [...avail.exceptions, newException].sort() });
                                setNewException("");
                            }
                        }}
                    >
                        Blokkeer datum
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-[420px]">
                <div>
                    <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">
                        Lead time (uren)
                    </label>
                    <input
                        type="number"
                        data-testid="lead-time"
                        value={avail.lead_time_uren}
                        onChange={(e) => setAvail({ ...avail, lead_time_uren: e.target.value })}
                        className="w-full rounded-[10px] border border-[var(--hds-line)] bg-[var(--hds-card)] px-3 py-2 text-[15px] outline-none focus:border-[var(--hds-clay)]"
                    />
                </div>
                <div>
                    <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">
                        Buffer (minuten)
                    </label>
                    <input
                        type="number"
                        data-testid="buffer"
                        value={avail.buffer_minuten}
                        onChange={(e) => setAvail({ ...avail, buffer_minuten: e.target.value })}
                        className="w-full rounded-[10px] border border-[var(--hds-line)] bg-[var(--hds-card)] px-3 py-2 text-[15px] outline-none focus:border-[var(--hds-clay)]"
                    />
                </div>
            </div>

            <div className="flex items-center gap-4">
                <Button variant="primary" onClick={save} data-testid="save-availability">
                    Opslaan
                </Button>
                {saved && <span className="text-[14px] text-[var(--hds-green)]" data-testid="save-confirm">Opgeslagen.</span>}
            </div>
        </div>
    );
}
